import React, { PropsWithChildren, useMemo } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { GenericTabs } from 'src/presentations/components/molecules/tabs/GenericTabs';
import { ITabOption } from 'src/presentations/components/molecules/tabs/types';
import { useTourContext } from 'src/presentations/guided-tours';

import { useShipmentSelector } from '../ShipmentSelector';
import InvoiceDetails from './InvoiceDetails';
import ShipmentOverview from './ShipmentOverview';
import LegDetails from './LegDetails';
import { useInvoiceContext } from '../InvoiceContextProvider';

interface ITabPanelProps {
  index: number;
  value: number;
}

const CustomTabPanel: React.FC<PropsWithChildren<ITabPanelProps>> = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div role='tabpanel' hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
      {value === index && (
        <Box>
          <Typography component='div'>{children}</Typography>
        </Box>
      )}
    </div>
  );
};

export const ActionDetailsTabsDetails = () => {
  const { tabDetails, setTabDetails } = useInvoiceContext();
  const { shipment, isLoadingShipment } = useShipmentSelector();
  const tour = useTourContext().appTour.registerTour('actionDetails');

  // Hide Leg Details tab when in No Rate Card review workflow
  const shouldHideLegDetails = shipment?.currentDeviationWorkflow === 'PROCUREMENT_NO_CONTRACT_REVIEW';

  // Calculate dynamic tab indices
  const TAB_INVOICE_DETAILS = 0;
  const TAB_SHIPMENT_OVERVIEW = 1;
  const TAB_LEG_DETAILS = 2;

  // Reset to Shipment Overview tab if currently on Leg Details and it gets hidden
  React.useEffect(() => {
    if (shouldHideLegDetails && tabDetails === TAB_LEG_DETAILS) {
      setTabDetails(TAB_SHIPMENT_OVERVIEW);
    }
  }, [shouldHideLegDetails, tabDetails, setTabDetails]);

  const tabOptions = useMemo<ITabOption[]>(() => {
    const options: ITabOption[] = [
      {
        value: String(TAB_INVOICE_DETAILS),
        label: 'Invoice Details',
      },
      {
        value: String(TAB_SHIPMENT_OVERVIEW),
        label: (
          <span className='flex items-center gap-8'>
            Shipment Overview
            {shipment?.status === 'SPOT_SHIPMENT' && <span className='text-[10px] py-2 px-4 font-semibold rounded-6 bg-neutral-10 text-neutral-50'>SPOT</span>}
          </span>
        ),
      },
    ];

    if (!shouldHideLegDetails) {
      options.push({
        value: String(TAB_LEG_DETAILS),
        label: 'Leg Details',
      });
    }

    return options;
  }, [shipment?.status, shouldHideLegDetails]);

  return (
    <Box className='bg-neutral-10 h-full flex flex-col'>
      {isLoadingShipment ? (
        <div className='w-full h-full flex items-center justify-center'>
          <CircularProgress color='primary' />
        </div>
      ) : (
        <>
          {shipment ? (
            <>
              <Box
                id={tour.registerStep('tabsInvoiceShipmentLeg')}
                className='flex justify-center bg-white px-14 xl:px-18 py-8 xl:py-10 border-b border-neutral-20'
              >
                <GenericTabs options={tabOptions} onChange={(value) => setTabDetails(Number(value))} defaultSelected={String(tabDetails)} className='!w-full' />
              </Box>
              <div className='px-16 xl:px-24 pt-10 pb-10 flex-1 overflow-auto mt-10 xl:mt-20'>
                <CustomTabPanel value={tabDetails} index={TAB_INVOICE_DETAILS}>
                  <InvoiceDetails />
                </CustomTabPanel>

                {/* start: Tab content - Shipment overview Detail */}
                <CustomTabPanel value={tabDetails} index={TAB_SHIPMENT_OVERVIEW}>
                  <ShipmentOverview />
                </CustomTabPanel>

                {/* start: Tab content - Leg Detail (hidden in No Rate Card review) */}
                {!shouldHideLegDetails && (
                  <CustomTabPanel value={tabDetails} index={TAB_LEG_DETAILS}>
                    <LegDetails />
                  </CustomTabPanel>
                )}
              </div>
            </>
          ) : (
            <div className='w-full h-full flex items-center justify-center'>
              <p className='text-16 uppercase text-center text-neutral-50'>Please select the shipment first.</p>
            </div>
          )}
        </>
      )}
    </Box>
  );
};
